import { Injectable } from '@angular/core';

import { CsvImportType, DateTimeFormatStrings, SpecialProcessCsvColumn } from "@shared/enums";
import { SiteConfigHelper } from "@shared/utils/site-config.helper";
import { Constants, CsvImportColumnName } from "@shared/constants";
import { Utilities } from "@shared/utils/utilities";

@Injectable({
  providedIn: 'root'
})
export class CsvService {

  constructor() { }

  // GetImportObjectFromCsvDataList = (type: CsvImportType, fileData: any[][]) => {
  //   var result: any[] = [];
  //   fileData.forEach(item => {
  //     result.push(this.GetImportObjectFromCsvData(type, item));
  //   });

  //   return result;
  // }

  GetImportObjectFromCsvData = (type: CsvImportType, fileData: any[]) => {
    if (!fileData || fileData.length === 0) {
      return [];
    }

    var config = SiteConfigHelper.GetCsvImportMapping(type);
    return fileData.map(rowData => {
      var singleObject: any = {};
      config.forEach(configItem => {
        var rowDataValue = rowData[configItem.csvKey];
        switch (configItem.storedKey) {
          case SpecialProcessCsvColumn.KPI_TIME:
            // for driving behavior
            var kpiTime = rowData[CsvImportColumnName.Time];
            var kpiDateTime = new Date(`${rowData[CsvImportColumnName.Date]} ${Utilities.isEmptyString(kpiTime) ? Constants.defaultTimeValue : kpiTime}`);
            singleObject[SpecialProcessCsvColumn.KPI_TIME] = Utilities.isValidDate(kpiDateTime) ?
              Utilities.formatDate(kpiDateTime, DateTimeFormatStrings.FORMAT_9) :
              `${rowData[CsvImportColumnName.Date]} ${Utilities.isEmptyString(kpiTime) ? "" : kpiTime}`;
            break;

          case SpecialProcessCsvColumn.GPS_MONITOR:
          case SpecialProcessCsvColumn.DESCRIPTION:
            singleObject[configItem.storedKey] = Utilities.isEmptyString(rowDataValue) ? "" : rowDataValue;
            break;

          case SpecialProcessCsvColumn.KPI_DATE:
            // for it tools usage
            if (!Utilities.isEmptyString(rowData[CsvImportColumnName.YEAR])) {
              singleObject[SpecialProcessCsvColumn.KPI_DATE] = this.formatDateValue(`${rowData[CsvImportColumnName.YEAR]}-${rowData[CsvImportColumnName.MONTH]}-01`);
            }
            else if (!Utilities.isEmptyString(rowDataValue)) {
              singleObject[SpecialProcessCsvColumn.KPI_DATE] = this.formatDateValue(`${rowDataValue}-01`);
            }

            break;

          case SpecialProcessCsvColumn.ACHIEVE_DATE:
            // for achievement
            var achieveDateValue = rowData[configItem.csvKey] as string;
            singleObject[configItem.storedKey] = this.formatDateValue(`${achieveDateValue}-01`);
            break;

          case SpecialProcessCsvColumn.AWARD_DATE:
            var awardDateValue = rowData[configItem.csvKey] as string;
            singleObject[configItem.storedKey] = this.formatDateValue(awardDateValue);
            break;

          case SpecialProcessCsvColumn.COMPLIANCE:
            if (Utilities.isEmptyString(singleObject[configItem.storedKey])) {
              if (Utilities.isEmptyString(rowDataValue)) {
                singleObject[configItem.storedKey] = null;
              } else if (rowDataValue.toString().toUpperCase() === "YES") {
                singleObject[configItem.storedKey] = true;
              }
              else if (rowDataValue.toString().toUpperCase() === "NO") {
                singleObject[configItem.storedKey] = false;
              }
              else {
                singleObject[configItem.storedKey] = null;
              }
            }

            break;

          case SpecialProcessCsvColumn.OCCURRENCE:
          case SpecialProcessCsvColumn.TARGETOCCURRENCE:
            if (!Utilities.isEmptyString(rowData[configItem.csvKey])) {
              singleObject[configItem.storedKey] = Number(rowData[configItem.csvKey]);
            }
            break;

          case SpecialProcessCsvColumn.ROWNUM:
          case SpecialProcessCsvColumn.KPISCORE:
          case SpecialProcessCsvColumn.KPIVALUE:
          case SpecialProcessCsvColumn.SPEED:
          case SpecialProcessCsvColumn.CYLINDER:
          case SpecialProcessCsvColumn.MILEAGE:
          case SpecialProcessCsvColumn.DURATION:
            if (!Utilities.isEmptyString(rowData[configItem.csvKey])) {
              singleObject[configItem.storedKey] = Number(rowData[configItem.csvKey].replace(',', ''));
            }
            else if (singleObject[configItem.storedKey] === undefined || singleObject[configItem.storedKey] === null) {
              singleObject[configItem.storedKey] = 0;
            }

            break;

          default:
            singleObject[configItem.storedKey] = rowData[configItem.csvKey] ?? "";
            break;
        }
      });

      if (singleObject[SpecialProcessCsvColumn.ROWNUM] === 0 || isNaN(singleObject[SpecialProcessCsvColumn.ROWNUM])) {
        return null;
      }

      return singleObject;
    }).filter(item => item !== null);
  }

  public GenerateDataArrayFromCSVText(csvText: string): Array<any> {
    var lineSplitter = csvText.includes("\r\n") ? "\r\n" : "\n";

    var headerText = this.clearStartEndComma(csvText.slice(0, csvText.indexOf(lineSplitter)));
    const propertyNames = this.splitLineTextToArray(headerText);

    const dataRows = csvText.slice(csvText.indexOf(lineSplitter) + lineSplitter.length).split(lineSplitter);

    let dataArray: any[] = [];
    dataRows.filter(row => !Utilities.isEmptyString(row)).forEach((row) => {
      if (row.startsWith(Constants.Comma)) {
        return;
      }

      let values = this.splitLineTextToArray(row);
      let obj: any = new Object();

      for (let index = 0; index < propertyNames.length; index++) {
        const propertyName: string = propertyNames[index];

        let val: any = values[index] ? this.removeStartEndQuota(values[index]) : '';
        if (val === '') {
          val = null;
        }

        obj[propertyName] = val;
      }

      dataArray.push(obj);
    });

    return dataArray;
  }

  private splitLineTextToArray(lineText: string) {
    while (lineText.includes(",,")) {
      lineText = lineText.replaceAll(",,", ",\"\",");
    }

    if (lineText.endsWith(",")) {
      lineText += '""';
    }

    return lineText.split('","').map(item => this.removeStartEndQuota(item).trim());
  }

  private clearStartEndComma(lineText: string) {
    if (Utilities.isEmptyString(lineText)) {
      return lineText;
    }

    while (lineText.startsWith(Constants.Comma)) {
      lineText = lineText.slice(1);
    }

    while (lineText.endsWith(Constants.Comma)) {
      lineText = lineText.slice(0, lineText.length - 1);
    }

    return lineText;
  }

  private removeStartEndQuota(lineText: string) {
    if (Utilities.isEmptyString(lineText)) {
      return lineText;
    }

    while (lineText.startsWith("'") || lineText.startsWith('"')) {
      lineText = lineText.slice(1);
    }


    while (lineText.endsWith("'") || lineText.endsWith('"')) {
      lineText = lineText.slice(0, lineText.length - 1);
    }

    return lineText;
  }

  private formatDateValue(dateValue: string) {
    var date = new Date(dateValue);
    if (!Utilities.isValidDate(date)) {
      return dateValue;
    }

    return Utilities.formatDate(date, DateTimeFormatStrings.FORMAT_6);
  }
}
